/**
 * Patients API Client
 *
 * Handles all patient-related API calls to Tebra EMR:
 * - Search for patients
 * - Create new patients
 * - Update existing patients
 */

import apiClient from './client';
import type {
  PatientSearchRequest,
  PatientSearchResponse,
  PatientCreateRequest,
  PatientCreateResponse,
  PatientUpdateRequest,
  PatientUpdateResponse,
} from '../types/api';

export const patientsApi = {
  /**
   * Search for patients in Tebra EMR
   */
  searchPatients: async (
    searchData: PatientSearchRequest,
    customerId: string,
    practiceId: string,
    emrConnectionId: string,
    sessionId: string
  ): Promise<PatientSearchResponse> => {
    const response = await apiClient.post('/api/v1/patients/search', searchData, {
      params: {
        customer_id: customerId,
        practice_id: practiceId,
        emr_connection_id: emrConnectionId,
        session_id: sessionId,
      },
    });
    return response.data;
  },

  /**
   * Fetch full patient details from Tebra (demographics, guarantor, insurance).
   * Matches desktop gui/main_window.py line 481: get_patient_raw_soap(patient_id)
   */
  getPatientDetails: async (
    patientId: number,
    customerId: string,
    practiceId: string,
    emrConnectionId: string,
    sessionId: string
  ): Promise<Record<string, any>> => {
    const response = await apiClient.get(`/api/v1/patients/${patientId}/details`, {
      params: {
        customer_id: customerId,
        practice_id: practiceId,
        emr_connection_id: emrConnectionId,
        session_id: sessionId,
      },
    });
    return response.data;
  },

  /**
   * Create a new patient in Tebra EMR
   */
  createPatient: async (
    patientData: PatientCreateRequest,
    customerId: string,
    practiceId: string,
    emrConnectionId: string,
    sessionId?: string
  ): Promise<PatientCreateResponse> => {
    const response = await apiClient.post('/api/v1/patients/', patientData, {
      params: {
        customer_id: customerId,
        practice_id: practiceId,
        emr_connection_id: emrConnectionId,
        ...(sessionId && { session_id: sessionId }),
      },
    });
    return response.data;
  },

  /**
   * Update an existing patient in Tebra EMR
   */
  updatePatient: async (
    patientId: number,
    updateData: PatientUpdateRequest,
    customerId: string,
    practiceId: string,
    emrConnectionId: string,
    sessionId?: string
  ): Promise<PatientUpdateResponse> => {
    const response = await apiClient.put(`/api/v1/patients/${patientId}`, updateData, {
      params: {
        customer_id: customerId,
        practice_id: practiceId,
        emr_connection_id: emrConnectionId,
        ...(sessionId && { session_id: sessionId }),
      },
    });
    return response.data;
  },
};
